import { useMemo } from 'react';
import styles from './CaseTypeBar.module.scss';
import { CASE_TYPE_COLOR, CASE_TYPE_LABEL, CaseTypeCategory, DEFAULT_BAR_PALETTE, DISPOSED_COLOR } from './caseTypeCategory';
import { Case, STATUS } from './legalCasesConstants';

const DISPOSED_SEGMENT_KEY = 'DISPOSED';

// Tie-break order for segments with equal counts — the enum's declaration order, so sorting stays
// deterministic across renders.
const CATEGORY_ORDER = Object.values(CaseTypeCategory);

type Segment = {
    key: CaseTypeCategory | typeof DISPOSED_SEGMENT_KEY;
    label: string;
    color: string;
    count: number;
};

// Presentational stacked bar for the active direction's HIGH-confidence cases only — low-confidence
// cases (Other Probable Cases group) are excluded from both the segments and the Total.
function CaseTypeBar({ cases }: { cases: Pick<Case, 'caseTypeCategory' | 'status'>[] }) {
    const segments = useMemo<Segment[]>(() => {
        const countByCategory = new Map<CaseTypeCategory, number>();
        let disposedCount = 0;

        cases.forEach((singleCase) => {
            if (singleCase.status === STATUS.DISPOSED.value) {
                disposedCount += 1;
                return;
            }
            // Until the backend sends caseTypeCategory, every active case falls to OTHER — expected;
            // the FE no longer guesses the category from standardCaseType/caseCategory. An
            // unrecognised backend string (not just null/undefined) must also fall back to OTHER so
            // it never reaches CASE_TYPE_LABEL/CASE_TYPE_COLOR as an unmapped key. Uses
            // hasOwnProperty (not `in`) so an inherited Object.prototype name like "toString" can
            // never pass this guard and reach CASE_TYPE_LABEL/CASE_TYPE_COLOR as a function value.
            const safeCategory =
                singleCase.caseTypeCategory && Object.prototype.hasOwnProperty.call(CASE_TYPE_LABEL, singleCase.caseTypeCategory)
                    ? singleCase.caseTypeCategory
                    : CaseTypeCategory.OTHER;
            countByCategory.set(safeCategory, (countByCategory.get(safeCategory) || 0) + 1);
        });

        // Sorted by descending count (tie-broken by enum order) so the color assignment below is
        // deterministic across renders — the top DEFAULT_BAR_PALETTE.length segments get the default
        // warm palette positionally; the rest fall back to their fixed CASE_TYPE_COLOR.
        const activeSegments: Segment[] = Array.from(countByCategory.entries())
            .filter(([, count]) => count > 0)
            .sort(([categoryA, countA], [categoryB, countB]) => {
                if (countB !== countA) return countB - countA;
                return CATEGORY_ORDER.indexOf(categoryA) - CATEGORY_ORDER.indexOf(categoryB);
            })
            .map(([category, count], index) => ({
                key: category,
                label: CASE_TYPE_LABEL[category],
                color: index < DEFAULT_BAR_PALETTE.length ? DEFAULT_BAR_PALETTE[index] : CASE_TYPE_COLOR[category],
                count,
            }));

        const disposedSegment: Segment[] =
            disposedCount > 0 ? [{ key: DISPOSED_SEGMENT_KEY, label: 'Disposed', color: DISPOSED_COLOR, count: disposedCount }] : [];

        return [...activeSegments, ...disposedSegment];
    }, [cases]);

    const total = segments.reduce((sum, segment) => sum + segment.count, 0);

    if (total === 0) return null;

    return (
        <div className={styles.CaseTypeBar}>
            <div className={styles.Bar}>
                {segments.map((segment) => (
                    <div
                        key={segment.key}
                        className={styles.BarSegment}
                        style={{ flexGrow: segment.count, backgroundColor: segment.color }}
                        title={`${segment.label}: ${segment.count}`}
                    />
                ))}
            </div>
            <div className={styles.Legend}>
                {segments.map((segment) => (
                    <span key={segment.key} className={styles.LegendItem}>
                        <span className={styles.LegendDot} style={{ backgroundColor: segment.color }} />
                        {segment.label}: {String(segment.count).padStart(2, '0')}
                    </span>
                ))}
                <span className={styles.Total}>Total: {total}</span>
            </div>
        </div>
    );
}

export default CaseTypeBar;
